import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import pnoeLogo from "../../common/images/logo.png";
import inBodyLogo from "./assets/inbody-red-logo.webp";
import AiSummaryRecommendationsView, {
  hasAiSummaryRecommendations,
} from "./AiSummaryRecommendationsView";
import {
  AiRmrInBodyMeasurement,
  AiRmrInBodyMetric,
  AiRmrInBodySummary,
} from "./aiRmrInBodySummaryTypes";
import classes from "./AiRmrInBodySummaryView.module.css";

type Props = {
  summary: AiRmrInBodySummary;
  variant?: "screen" | "pdf";
};

type MetricTone = "danger" | "warning" | "neutral" | "success";

type ConnectionDefinition = {
  title: string;
  primary?: AiRmrInBodyMetric;
  secondary?: AiRmrInBodyMeasurement;
  description: string;
};

const INBODY_BRAND_STYLE = {
  "--rmr-inbody-red": "#971b2f",
  "--rmr-inbody-charcoal": "#191919",
  "--rmr-inbody-slate": "#46586b",
} as React.CSSProperties;

const clampPercent = (value: number): number =>
  Math.max(0, Math.min(100, value));

const formatNumber = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);

const formatDate = (value: string): string => {
  if (!value) {
    return "";
  }

  const shortDate = value.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  const date = shortDate
    ? new Date(
        Number(shortDate[3]),
        Number(shortDate[1]) - 1,
        Number(shortDate[2])
      )
    : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const normalizeLabel = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const findMetric = (
  metrics: AiRmrInBodyMetric[],
  id: string,
  label: string
): AiRmrInBodyMetric | undefined =>
  metrics.find((metric) => metric.id.toLowerCase() === id.toLowerCase()) ??
  metrics.find((metric) => normalizeLabel(metric.label) === label);

const findMeasurement = (
  measurements: AiRmrInBodyMeasurement[],
  label: string
): AiRmrInBodyMeasurement | undefined =>
  measurements.find(
    (measurement) => normalizeLabel(measurement.label) === label
  );

const getMetricTone = (scoreCategory?: number): MetricTone => {
  if (scoreCategory === 1) {
    return "danger";
  }
  if (scoreCategory === 2) {
    return "warning";
  }
  if (scoreCategory === 4 || scoreCategory === 5) {
    return "success";
  }
  return "neutral";
};

const metricToneClass: Record<MetricTone, string> = {
  danger: classes.toneDanger,
  warning: classes.toneWarning,
  neutral: classes.toneNeutral,
  success: classes.toneSuccess,
};

const MetricValue = ({ metric }: { metric: AiRmrInBodyMetric }) => (
  <div className={classes.connectionMetric}>
    <span>{metric.label}</span>
    <p>
      <strong>{formatNumber(metric.value)}</strong>
      {metric.unit && <small>{metric.unit}</small>}
      {metric.scoreCategoryLabel && <em>{metric.scoreCategoryLabel}</em>}
    </p>
  </div>
);

const MeasurementValue = ({
  measurement,
}: {
  measurement: AiRmrInBodyMeasurement;
}) => (
  <div className={classes.connectionMetric}>
    <span>{measurement.label}</span>
    <p>
      <strong>{formatNumber(measurement.value)}</strong>
      {measurement.unit && <small>{measurement.unit}</small>}
    </p>
  </div>
);

const ConnectionCard = ({
  connection,
}: {
  connection: ConnectionDefinition;
}) => (
  <article className={classes.connectionCard} data-pdf-break="after">
    <h3>{connection.title}</h3>
    <div className={classes.connectionValues}>
      {connection.primary && <MetricValue metric={connection.primary} />}
      {connection.secondary && (
        <MeasurementValue measurement={connection.secondary} />
      )}
    </div>
    {connection.description && <p>{connection.description}</p>}
  </article>
);

const OverviewMetricCard = ({ metric }: { metric: AiRmrInBodyMetric }) => {
  const tone = getMetricTone(metric.scoreCategory);
  const showProgress = metric.unit === "%";

  return (
    <article className={`${classes.metricCard} ${metricToneClass[tone]}`}>
      <div className={classes.metricHeader}>
        <span>{metric.label}</span>
        {metric.scoreCategoryLabel && <em>{metric.scoreCategoryLabel}</em>}
      </div>
      <p>
        <strong>{formatNumber(metric.value)}</strong>
        {metric.unit && <small>{metric.unit}</small>}
      </p>
      {showProgress && (
        <div
          className={classes.metricTrack}
          role="progressbar"
          aria-label={`${metric.label} score`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={clampPercent(metric.value)}
        >
          <i style={{ width: `${clampPercent(metric.value)}%` }} />
        </div>
      )}
    </article>
  );
};

const SectionHeading = ({ title }: { title: string }) => (
  <div className={classes.sectionHeading}>
    <h2>{title}</h2>
    <i />
  </div>
);

const AiRmrInBodySummaryView = ({ summary, variant = "screen" }: Props) => {
  const connections: ConnectionDefinition[] = [
    {
      title: "Metabolic rate & muscle mass",
      primary: findMetric(summary.overviewMetrics, "11_RMR", "metabolic rate"),
      secondary: findMeasurement(
        summary.bodyComposition,
        "skeletal muscle mass"
      ),
      description: summary.rmrMuscleDescription,
    },
    {
      title: "Fat burning & body fat",
      primary: findMetric(
        summary.overviewMetrics,
        "3_RMR",
        "fat burn efficiency"
      ),
      secondary: findMeasurement(
        summary.bodyComposition,
        "body fat percentage"
      ),
      description: summary.fatBurnBodyFatDescription,
    },
    {
      title: "Fuel switching & cellular health",
      primary: findMetric(
        summary.overviewMetrics,
        "8_RMR",
        "metabolic flexibility"
      ),
      secondary: findMeasurement(
        summary.bodyComposition,
        "whole body phase angle"
      ),
      description: summary.flexibilityPhaseAngleDescription,
    },
  ].filter(
    (connection) =>
      connection.primary || connection.secondary || connection.description
  );
  const hasIdentity = Boolean(
    summary.patient.name ||
      summary.patient.companyName ||
      summary.patient.rmrTestDate ||
      summary.patient.inBodyTestDate ||
      summary.reportType
  );
  const hasPnoeMeasurements = Boolean(
    summary.hero.rmr ||
      summary.hero.fatPercent !== undefined ||
      summary.hero.carbPercent !== undefined ||
      summary.overviewMetrics.length > 0
  );
  const hasRecommendations = hasAiSummaryRecommendations({
    trainingPlan: summary.trainingPlan,
    nutrition: summary.nutrition,
    recommendations: summary.recommendations,
  });
  const hasContent = Boolean(
    hasIdentity ||
      connections.length > 0 ||
      summary.bodyComposition.length > 0 ||
      hasPnoeMeasurements ||
      hasRecommendations ||
      summary.disclaimer
  );
  const fatPercent = clampPercent(summary.hero.fatPercent ?? 0);
  const carbPercent = clampPercent(summary.hero.carbPercent ?? 0);
  const fuelTotal = fatPercent + carbPercent || 1;

  if (!hasContent) {
    return (
      <section className={classes.emptyState}>
        <h3>No RMR + InBody summary content was returned</h3>
        <p>
          The AI response did not include any displayable RMR or InBody fields.
        </p>
      </section>
    );
  }

  return (
    <div
      className={`${classes.report} ${classes[variant]}`}
      style={INBODY_BRAND_STYLE}
    >
      <header className={classes.hero} data-pdf-break="after">
        <div className={classes.brandPanel}>
          <img className={classes.pnoeLogo} src={pnoeLogo} alt="PNOE" />
          <span className={classes.brandConnector}>+</span>
          <span className={classes.inBodyLogoPanel}>
            <img src={inBodyLogo} alt="InBody" />
          </span>
          <p>Clinical intelligence</p>
        </div>
        <div className={classes.heroIdentity}>
          {summary.patient.companyName && (
            <span>{summary.patient.companyName}</span>
          )}
          <h1>RMR &amp; InBody Summary</h1>
          {summary.patient.name && <h2>{summary.patient.name}</h2>}
          <div className={classes.testDates}>
            {summary.patient.rmrTestDate && (
              <p>RMR test: {formatDate(summary.patient.rmrTestDate)}</p>
            )}
            {summary.patient.inBodyTestDate && (
              <p>InBody test: {formatDate(summary.patient.inBodyTestDate)}</p>
            )}
          </div>
          {summary.reportType && <em>{summary.reportType}</em>}
        </div>
      </header>

      {connections.length > 0 && (
        <section className={classes.reportSection}>
          <SectionHeading
            title={summary.connectionsTitle || "PNOĒ - InBody Connections"}
          />
          <div className={classes.connectionGrid}>
            {connections.map((connection) => (
              <ConnectionCard key={connection.title} connection={connection} />
            ))}
          </div>
        </section>
      )}

      {summary.bodyComposition.length > 0 && (
        <section className={classes.reportSection} data-pdf-break="after">
          <SectionHeading
            title={summary.inBodyMeasurementsTitle || "InBody Measurements"}
          />
          <div className={classes.measurementGrid}>
            {summary.bodyComposition.map((measurement, index) => (
              <article
                key={`${measurement.label}-${index}`}
                className={classes.measurementCard}
              >
                <span>{measurement.label}</span>
                <p>
                  <strong>{formatNumber(measurement.value)}</strong>
                  {measurement.unit && <small>{measurement.unit}</small>}
                </p>
              </article>
            ))}
          </div>
        </section>
      )}

      {hasPnoeMeasurements && (
        <section className={classes.reportSection}>
          <SectionHeading
            title={summary.pnoeMeasurementsTitle || "PNOĒ Measurements"}
          />
          <div className={classes.pnoeGrid} data-pdf-break="after">
            {summary.hero.rmr && (
              <article className={classes.rmrCard}>
                <span>Resting metabolic rate</span>
                <p>
                  <strong>{formatNumber(summary.hero.rmr.value)}</strong>
                  {summary.hero.rmr.unit && (
                    <small>{summary.hero.rmr.unit}</small>
                  )}
                </p>
              </article>
            )}
            {(summary.hero.fatPercent !== undefined ||
              summary.hero.carbPercent !== undefined) && (
              <article className={classes.fuelCard}>
                <span>Fuel sources at rest</span>
                <div
                  className={classes.fuelBar}
                  aria-label="Fuel source distribution"
                >
                  <i style={{ width: `${(fatPercent / fuelTotal) * 100}%` }} />
                  <b style={{ width: `${(carbPercent / fuelTotal) * 100}%` }} />
                </div>
                <div className={classes.fuelLegend}>
                  {summary.hero.fatPercent !== undefined && (
                    <span className={classes.fatLegend}>
                      Fats {formatNumber(summary.hero.fatPercent)}%
                    </span>
                  )}
                  {summary.hero.carbPercent !== undefined && (
                    <span className={classes.carbLegend}>
                      Carbs {formatNumber(summary.hero.carbPercent)}%
                    </span>
                  )}
                </div>
              </article>
            )}
          </div>
          {summary.overviewMetrics.length > 0 && (
            <div className={classes.metricGrid}>
              {summary.overviewMetrics.map((metric, index) => (
                <OverviewMetricCard
                  key={`${metric.id}-${index}`}
                  metric={metric}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {hasRecommendations && (
        <section className={classes.reportSection}>
          <SectionHeading title="Recommendations" />
          <AiSummaryRecommendationsView
            trainingPlan={summary.trainingPlan}
            nutrition={summary.nutrition}
            recommendations={summary.recommendations}
            variant={variant}
          />
        </section>
      )}

      {summary.disclaimer && (
        <section className={classes.disclaimer} data-pdf-break="after">
          <InfoOutlinedIcon aria-hidden="true" />
          <div>
            <strong>Disclaimer</strong>
            <p>{summary.disclaimer}</p>
          </div>
        </section>
      )}
    </div>
  );
};

export default AiRmrInBodySummaryView;
